import React from 'react';
import { Trans } from '@lingui/react/macro';

import HowBrandWorks from '@rover/components/src/HowBrandWorks';
import DogTrainingLandingSearchForm from '@rover/react-lib/src/pages/landing/components/DogTrainingLandingSearchForm';
import LandingHelmet from '@rover/react-lib/src/pages/landing/components/LandingHelmet';
import LandingHero, {
  HeroImageSrcSet,
} from '@rover/react-lib/src/pages/landing/components/LandingHero';
import ServiceLandingServicesWrapper from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceLandingServicesWrapper';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import {
  ROVER_DATA_CITATION,
  Superscript,
} from '@rover/react-lib/src/pages/shared/FooterLinks/FactCitations';
import Footer from '@rover/react-lib/src/pages/shared/FooterLinks/Footer';
import { ServiceLandingEnum } from '@rover/types/src/ServiceLanding';

import QuickFact, { QuickFactHighlight } from '../../../SeoLocation/components/QuickFact';
import servicesLinksImage from '../../components/images/dog-looking-upward.png';
import RoverProtectCondensed from '../../components/RoverProtectCondensed';
import ServiceBenefits from '../../components/ServiceBenefits';
import DogTrainingServiceFaqs from '../../components/ServiceFaqs/DogTrainingServiceFaqs';
import ServiceLandingSection, {
  SERVICE_LANDING_SECTION_MARGIN_Y,
} from '../../components/ServiceLandingSection';
import { quickFactStickerChihuahua, quickFactStickerPug } from '../shared-images';

import dogTrainingHeroDesktop from './images/training-hero-desktop.jpg';
import dogTrainingHeroTablet from './images/training-hero-tablet.jpg';

const dogTrainingHeroImages: HeroImageSrcSet = {
  desktop: {
    src: dogTrainingHeroDesktop,
    height: 500,
  },
  tablet: {
    src: dogTrainingHeroTablet,
    height: 500,
  },
};

/* DEV-104195 TODO: translate this entire page before global rollout */
const DogTraining: React.FC<Props> = ({
  countryConfig,
  serviceLandingData,
  serviceLinksWithDisplayName,
  citiesAndNeighborhoodsLinks,
  countryFooterLinks,
  countryRegionLinks,
}) => {
  const serviceLandingType = ServiceLandingEnum.DOG_TRAINING;

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;

  // This is a temporary quick fact number. Eventually this will be replaced with fact data from the database
  const NUM_DOGS = 4100;
  const numDogsFormatted = NUM_DOGS.toLocaleString();

  return (
    <>
      <LandingHelmet title={seoTitle} metaDescription={seoMetaDescription} />
      <LandingHero heroImageSrcSet={dogTrainingHeroImages} upliftedContent>
        <LandingHero.Content upliftedContent>
          <LandingHero.Display mt="0x" mb="8x">
            {h1}
          </LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <DogTrainingLandingSearchForm showSearchFilters={false} />
        </LandingHero.Content>
      </LandingHero>

      {/* Includes Section within component */}
      <RoverProtectCondensed
        countryConfig={countryConfig}
        mb={SERVICE_LANDING_SECTION_MARGIN_Y}
        isTraining
      />

      {/* Includes Section & margins within component */}
      <ServiceBenefits title="Private dog training at your dog’s pace">
        <ServiceBenefits.List>
          <ServiceBenefits.ListItem title="Find personalized dog training to meet your goals">
            Get training tailored to your dog’s needs, whether you’re teaching your puppy basic
            manners or working with your dog to master an advanced skill.
          </ServiceBenefits.ListItem>
          <ServiceBenefits.ListItem title="Dog training where and when you need it">
            Chat with your trainer about where to train, plan your sessions, and track your pet’s
            progress, all in one place. And if you prefer virtual training, that’s an option, too!
          </ServiceBenefits.ListItem>
          <ServiceBenefits.ListItem title="Trainers you can trust">
            Trainers on Rover show credentials, commit to rewards-based training, and pass a
            third-party background check. That way, you can focus on experience and specific skills
            to find the right one for you.
          </ServiceBenefits.ListItem>
        </ServiceBenefits.List>
      </ServiceBenefits>

      {/* Includes Section & margins within component */}
      <QuickFact
        my={SERVICE_LANDING_SECTION_MARGIN_Y}
        themeColor="yellow"
        stickerImageSrc={quickFactStickerChihuahua}
      >
        <Trans>
          Over <QuickFactHighlight>{numDogsFormatted}</QuickFactHighlight> dogs (and their humans!)
          leveled up their skills, thanks to dog trainers on Rover
        </Trans>
        <Superscript>1</Superscript>
      </QuickFact>

      <ServiceLandingSection>
        <DogTrainingServiceFaqs />
      </ServiceLandingSection>

      {/* Includes Section & margins within component */}
      <QuickFact
        my={SERVICE_LANDING_SECTION_MARGIN_Y}
        themeColor="pink"
        stickerImageSrc={quickFactStickerPug}
      >
        <Trans>
          Dogs show <QuickFactHighlight>more positivity</QuickFactHighlight> when trained with
          reward-based methods
        </Trans>
        <Superscript>2</Superscript>
      </QuickFact>

      <HowBrandWorks>
        <HowBrandWorks.Display>How to book the perfect trainer</HowBrandWorks.Display>
        <HowBrandWorks.List>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={1}>Search</HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              Read verified reviews and contact trainers who look like a good match.
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={2}>Meet trainers</HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              Chat about your training goals and how they can help you and your dog.
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={3}>Schedule sessions</HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              Schedule training sessions and pay through Rover's secure platform.
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
        </HowBrandWorks.List>
      </HowBrandWorks>

      {/* Includes Section & margins within component */}
      <ServiceLandingServicesWrapper
        serviceLandingType={serviceLandingType}
        serviceLinksWithDisplayName={serviceLinksWithDisplayName}
        imageSrc={servicesLinksImage}
      >
        Rover offers many services to meet your pet care needs, including:
      </ServiceLandingServicesWrapper>

      <ServiceLandingSection mb={['0x', '0x']} data-qa-id="service-landing-download-app-callout">
        <AppStoreCallout />
      </ServiceLandingSection>

      <Footer
        citiesAndNeighborhoodsLinks={citiesAndNeighborhoodsLinks}
        countryFooterLinks={countryFooterLinks}
        countryRegionLinks={countryRegionLinks}
        citations={[
          ROVER_DATA_CITATION(),
          <>
            Casey et al. (2021).{' '}
            <cite>Dogs are more pessimistic if owners use multiple aversive training methods.</cite>{' '}
            Animals Journal.
          </>,
        ]}
      />
    </>
  );
};

export default DogTraining;
